import useCoachStore from "../../store/coachStore.js";
import { useEffect } from "react";
import useSiteStore from "../../store/siteStore.js";
import useProfileStore from "../../store/profileStore.js";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Chip, Tooltip, CircularProgress } from '@mui/material';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { formatTrainingFocus } from "../../utils/index.js";
import { defaultColorScheme, tealColorScheme, darkTheme } from '../../styles/colorSchemes/index.js';
import { useAppTheme } from '../../hooks/useAppTheme.js';

function WeeklySummaryOfSelectedAthletes() {

    const { getWeeklyScheduleOfCheckedAthletes, checkedAthletes, setCheckedAthletes, weeklyScheduleOfCheckedAthletes, loadingWeeklySchedule } = useCoachStore();
    const { year, selectedCalenderWeek } = useSiteStore();
    const { colorScheme, profile, profileType } = useProfileStore();
    const { isDarkMode } = useAppTheme();

    let colors = defaultColorScheme.trainingFocusColor;
    if (colorScheme === 'teal') {
        colors = tealColorScheme.trainingFocusColor;
    }

    // eslint-disable-next-line
    const getContrastTextColor = (hexColor) => {
        if (!hexColor || !hexColor.startsWith('#')) return 'white';
        const r = parseInt(hexColor.substring(1, 3), 16);
        const g = parseInt(hexColor.substring(3, 5), 16);
        const b = parseInt(hexColor.substring(5, 7), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? 'rgba(0, 0, 0, 0.87)' : 'white';
    };

    useEffect(() => {
        if (profileType === 'athlete' && profile) {
            setCheckedAthletes([{ profile: profile }]);
        }
    }, [profileType, profile, setCheckedAthletes]);

    useEffect(() => {
        if (checkedAthletes.length > 0) {
            getWeeklyScheduleOfCheckedAthletes(year, selectedCalenderWeek);
        }
    }, [year, selectedCalenderWeek, getWeeklyScheduleOfCheckedAthletes, checkedAthletes]);

    const createTableCells = (sessions) => {
        if (!sessions) return "-";

        const tooltipContent = (
            <Box sx={{ overflow: 'hidden', borderRadius: '8px' }}>
                {sessions.map((s, index) => {
                    const focusColor = (s.trainingFocus >= 1 && colors[s.trainingFocus - 1]) 
                        ? colors[s.trainingFocus - 1] 
                        : isDarkMode ? darkTheme.accent : '#1976d2';

                    return (
                        <Box 
                            key={index} 
                            sx={{ 
                                mb: index < sessions.length - 1 ? 0 : 0,
                                borderBottom: index < sessions.length - 1 
                                    ? `1px solid ${isDarkMode ? darkTheme.cardBorder : '#e0e0e0'}` 
                                    : 'none',
                            }}
                        >
                            {/* Colored Header */}
                            <Box 
                                sx={{ 
                                    bgcolor: focusColor,
                                    px: 1.5,
                                    py: 0.75,
                                    textAlign: 'center',
                                }}
                            >
                                <Typography 
                                    variant="subtitle2" 
                                    fontWeight={600}
                                    sx={{ 
                                        color: isDarkMode ? 'rgba(0, 0, 0, 0.87)' : 'white',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    {formatTrainingFocus(s.trainingFocus)}
                                </Typography>
                            </Box>

                            {/* Body with icons */}
                            <Box sx={{ px: 1.5, py: 1, minWidth: '120px' }}>
                                {/* Duration row */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill={isDarkMode ? '#9e9e9e' : '#666'}>
                                            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                        </svg>
                                        <Typography variant="caption" sx={{ color: isDarkMode ? darkTheme.textSecondary : '#666', fontSize: '0.75rem' }}>
                                            Dauer
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" fontWeight={600} sx={{ color: isDarkMode ? darkTheme.textPrimary : 'rgba(0,0,0,0.87)', fontSize: '0.75rem' }}>
                                        {s.duration} min
                                    </Typography>
                                </Box>

                                {/* Intensity row */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: s.description ? 0.5 : 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill={isDarkMode ? '#9e9e9e' : '#666'}>
                                            <path d="M11.57 13.16c-1.36.28-2.17 1.16-2.17 2.41 0 1.34 1.11 2.42 2.49 2.42 2.05 0 3.71-1.66 3.71-3.71 0-1.07-.15-2.12-.46-3.12-.79 1.07-2.2 1.72-3.57 2zM13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z"/>
                                        </svg>
                                        <Typography variant="caption" sx={{ color: isDarkMode ? darkTheme.textSecondary : '#666', fontSize: '0.75rem' }}>
                                            Intensität
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" fontWeight={600} sx={{ color: isDarkMode ? darkTheme.textPrimary : 'rgba(0,0,0,0.87)', fontSize: '0.75rem' }}>
                                        {s.intensity}/10
                                    </Typography>
                                </Box>

                                {/* Description */}
                                {s.description && (
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            display: 'block',
                                            mt: 0.5,
                                            pt: 0.5,
                                            borderTop: `1px dashed ${isDarkMode ? darkTheme.cardBorder : '#e0e0e0'}`,
                                            color: isDarkMode ? darkTheme.textSecondary : '#666',
                                            fontSize: '0.7rem',
                                            fontStyle: 'italic',
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {s.description.substring(0, 60)}{s.description.length > 60 ? '...' : ''}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );

        const isSingle = sessions.length === 1;
        const label = isSingle
            ? formatTrainingFocus(sessions[0].trainingFocus)
            : `${sessions.length}x`;

        let chipSx = {};
        let chipVariant = "outlined";

        if (isSingle) {
            const focusId = sessions[0].trainingFocus;
            if (focusId >= 1 && colors[focusId - 1]) {
                const bgColor = colors[focusId - 1];
                chipSx = {
                    bgcolor: bgColor,
                    color: isDarkMode ? 'rgba(0, 0, 0, 0.87)' : 'white',
                    border: 'none',
                    fontWeight: 500
                };
                chipVariant = "filled";
            }
        }

        return (
            <Tooltip 
                title={tooltipContent} 
                placement="top"
                componentsProps={{ 
                    tooltip: { 
                        sx: { 
                            bgcolor: isDarkMode ? darkTheme.cardBg : 'common.white', 
                            color: isDarkMode ? darkTheme.textPrimary : 'common.black', 
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            border: `1px solid ${isDarkMode ? darkTheme.cardBorder : '#e0e0e0'}`, 
                            borderRadius: '8px',
                            p: 0,
                            overflow: 'hidden',
                            maxWidth: 280,
                        } 
                    }
                }}
            >
                <Chip 
                    label={label} 
                    size="small" 
                    variant={chipVariant} 
                    sx={{
                        ...chipSx,
                        ...(chipVariant === 'outlined' && {
                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                            borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
                        }),
                        // Larger chips on mobile for better readability
                        '@media (max-width: 600px)': {
                            height: '28px',
                            fontSize: '0.75rem',
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                                px: 1,
                                whiteSpace: 'nowrap',
                            }
                        }
                    }} 
                />
            </Tooltip>
        );
    }

    return (
        <Accordion 
            defaultExpanded={true}
            sx={{ 
                bgcolor: isDarkMode ? darkTheme.accordionBg : '#ffffff',
                borderRadius: '12px !important',
                border: `1px solid ${isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.08)'}`,
                boxShadow: isDarkMode 
                    ? '0 4px 20px rgba(0, 0, 0, 0.25)' 
                    : '0 4px 20px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { 
                    margin: 0,
                },
            }}
        >
            <AccordionSummary 
                expandIcon={
                    <ExpandMoreOutlined 
                        sx={{ 
                            color: isDarkMode ? darkTheme.textSecondary : 'rgba(0, 0, 0, 0.54)',
                            transition: 'transform 0.3s ease',
                        }} 
                    />
                }
                sx={{
                    bgcolor: isDarkMode ? darkTheme.cardBg : '#ffffff',
                    borderBottom: `1px solid ${isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.06)'}`,
                    minHeight: '56px',
                    '&.Mui-expanded': {
                        minHeight: '56px',
                    },
                    '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                    },
                    '&:hover': {
                        bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.04)',
                    },
                    transition: 'background-color 0.2s ease',
                }}
            >
                <Typography 
                    sx={{ 
                        color: isDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        letterSpacing: '0.01em',
                    }}
                >
                    Bereits vorhandene Trainingsseinheiten
                </Typography>
            </AccordionSummary>
            <AccordionDetails 
                sx={{ 
                    bgcolor: isDarkMode ? darkTheme.accordionBg : '#f5f5f5',
                    p: 2,
                }}
            >
                {loadingWeeklySchedule ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress 
                            size={36} 
                            sx={{ 
                                color: isDarkMode ? darkTheme.accent : undefined 
                            }} 
                        />
                    </Box>
                ) : (
                    <TableContainer 
                        component={Paper} 
                        sx={{ 
                            width: '100%',
                            bgcolor: isDarkMode ? darkTheme.headerBg : '#f5f5f5',
                            backgroundImage: 'none',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.08)'}`,
                            boxShadow: isDarkMode 
                                ? '0 2px 12px rgba(0, 0, 0, 0.2)' 
                                : '0 2px 12px rgba(0, 0, 0, 0.06)',
                            overflowX: 'auto',
                        }}
                    >
                        <Table sx={{ minWidth: 800, tableLayout: 'fixed' }} size="small" aria-label="training sessions table">
                            <TableHead>
                                <TableRow
                                    sx={{
                                        bgcolor: isDarkMode ? darkTheme.cardBg : '#ffffff',
                                    }}
                                >
                                    <TableCell 
                                        sx={{ 
                                            fontWeight: 700, 
                                            width: '16%', 
                                            color: isDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)', 
                                            borderBottom: `2px solid ${isDarkMode ? darkTheme.accent : '#1976d2'}`,
                                            py: 1.5,
                                            fontSize: '0.875rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                        }}
                                    >
                                        Athlet
                                    </TableCell>
                                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day, index) => (
                                        <TableCell 
                                            key={day} 
                                            align="center" 
                                            sx={{ 
                                                width: '12%', 
                                                fontWeight: 700, 
                                                color: isDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)', 
                                                borderBottom: `2px solid ${isDarkMode ? darkTheme.accent : '#1976d2'}`,
                                                py: 1.5,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            {day}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {checkedAthletes.map((athlete, rowIndex) => {
                                    const trainingSessions = weeklyScheduleOfCheckedAthletes[athlete.profile.email]?.trainingSessions || [];
                                    const sessionsByDay = trainingSessions.reduce((acc, session) => {
                                        if (!acc[session.dayOfWeek]) acc[session.dayOfWeek] = []
                                        acc[session.dayOfWeek].push(session);
                                        return acc;
                                    }, {});
                                    // eslint-disable-next-line
                                    const isLastRow = rowIndex === checkedAthletes.length - 1;
                                    return (
                                        <TableRow 
                                            key={athlete.profile.email} 
                                            sx={{ 
                                                '&:last-child td, &:last-child th': { border: 0 },
                                            }}
                                        >
                                            <TableCell 
                                                sx={{ 
                                                    color: isDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)', 
                                                    borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.08)',
                                                    py: 1.5,
                                                    fontWeight: 500,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                {`${athlete.profile.firstName} ${athlete.profile.lastName}`}
                                            </TableCell>
                                            {[1, 2, 3, 4, 5, 6, 7].map(day => (
                                                <TableCell 
                                                    align="center" 
                                                    key={day} 
                                                    sx={{ 
                                                        color: isDarkMode ? darkTheme.textSecondary : 'rgba(0, 0, 0, 0.6)', 
                                                        borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.08)',
                                                        py: 1.5,
                                                    }}
                                                >
                                                    {createTableCells(sessionsByDay[day])}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

export default WeeklySummaryOfSelectedAthletes;